import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import FORMATTERS from 'helpers/formatters';

function LineChart({ data, formatNumber = false }) {
  const calculateTickInterval = useMemo(() => {
    if (!data?.series || !data.series[0]?.data || data.series[0].data.length === 0) {
      return 100;
    }

    let min = Infinity;
    let max = -Infinity;

    data.series.forEach(series => {
      if (series.data && series.data.length) {
        const seriesMin = Math.min(
          ...series.data.filter(val => val !== null && val !== undefined),
        );
        const seriesMax = Math.max(
          ...series.data.filter(val => val !== null && val !== undefined),
        );

        min = Math.min(min, seriesMin);
        max = Math.max(max, seriesMax);
      }
    });

    if (min === Infinity || max === -Infinity) {
      return 100;
    }

    const range = max - min;

    const targetTicks = 5;
    let rawInterval = range / targetTicks;

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
    const magnitudeMultipliers = [1, 2, 2.5, 5, 10];

    let bestMultiplier = magnitudeMultipliers[0];
    for (const multiplier of magnitudeMultipliers) {
      if (rawInterval <= multiplier * magnitude) {
        bestMultiplier = multiplier;
        break;
      }
    }

    return Math.max(magnitude * bestMultiplier, 1);
  }, [data]);

  const defaultOptions = useMemo(
    () => ({
      chart: {
        plotShadow: false,
        spacingTop: 30,
        spacingBottom: 30,
        spacingLeft: 24,
        spacingRight: 24,
        style: {
          fontFamily: 'Poppins',
        },
        height: 300,
      },
      lang: {
        decimalPoint: ',',
        thousandsSep: '.',
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      title: {
        text: '',
      },
      legend: {
        enabled: false,
      },
      yAxis: {
        title: false,
        tickInterval: calculateTickInterval,
        labels: {
          formatter() {
            return `<span class="financial-value">${formatNumber ? FORMATTERS.NUMBER(this.value) : this.value} </span>`;
          },
          style: {
            color: '#6b7280',
            fontSize: '11px',
          },
        },
        lineColor: '#e5e7eb',
        gridLineColor: '#e5e7eb',
        gridLineWidth: 0.5,
        minorGridLineColor: '#f3f4f6',
        minorGridLineWidth: 0,
        minorTickLength: 0,
        minorTickInterval: null,
      },
      xAxis: {
        categories: data.categories,
        lineColor: '#e5e7eb',
        tickColor: '#e5e7eb',
        gridLineWidth: 0,
        tickmarkPlacement: 'on',
        labels: {
          style: {
            color: '#6b7280',
            fontSize: '11px',
          },
        },
      },
      tooltip: {
        borderRadius: 6,
        backgroundColor: '#fff',
        borderWidth: 1,
        borderColor: '#e5e7eb',
        style: {
          fontFamily: 'Poppins',
          fontSize: '12px',
        },
        formatter() {
          let content = '';
          content += '<div style="padding: 2px 0;">';
          content += `<span style="color: #8a70d6; font-weight: 500;">`;
          content += formatNumber ? FORMATTERS.NUMBER(this.y) : this.y;
          content += '</span>';
          content += '<br>';
          content += `<span style="color: #6b7280; font-size: 11px;">${this.x}</span>`;
          content += '</div>';

          return content;
        },
        shadow: false,
        followTouchMove: false,
        followPointer: true,
        crosshairs: [
          {
            color: '#e5e7eb',
            dashStyle: 'solid',
            width: 1,
          },
        ],
      },
      plotOptions: {
        series: {
          shadow: false,
          animation: true,
          cursor: 'pointer',
          lineWidth: 2.5,
          color: '#8a70d6',
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4,
                fillColor: '#FFFFFF',
                lineWidth: 2,
                lineColor: '#8a70d6',
                symbol: 'circle',
              },
            },
          },
          states: {
            hover: {
              lineWidthPlus: 0,
              halo: {
                size: 0,
              },
            },
          },
          events: {
            legendItemClick() {
              return true;
            },
          },
        },
        area: {
          tooltip: {
            followTouchMove: false,
          },
        },
      },
      series: data.series,
    }),
    [data, formatNumber, calculateTickInterval],
  );

  return <HighchartsReact highcharts={Highcharts} options={defaultOptions} />;
}

export default LineChart;
